import type {
  ConditionGroupMap,
  ConditionMap
} from "../components/RulesUI/Constraints/constraints.types";
import { getRulesModel } from "./rules.model";
import { uuidWithProcessId } from "../utils/db.utils";
import type {
  Field,
  Process,
  RuleMap,
  Step,
  StepTypes
} from "@certa/studio-core";
import { RuleType } from "@certa/studio-core";
import { fieldModelCreator } from "./field.model";
import type { ZodStep } from "@certa/queries/schemas/studio/deployableJSON.schema";
import type { ZodStepAppJSON } from "@certa/queries/schemas/studio/appProcessJSON.schema";

/**
 * TODO:
 * - Handle unhandled props
 * - Handle Data inheritance migration from v1 to v2
 */

const constraintRuleTypeMapping: Record<string, RuleType> = {
  accessGroupConstraints: RuleType.StepAccess,
  notifications: RuleType.Notification,
  pdfConstraints: RuleType.PDFGeneration,
  stepActionConstraints: RuleType.StepAction,
  visibilityConstraints: RuleType.StepVisibility,
  versionNamingConstraints: RuleType.VersionNamingConstraints
};

const constraintRawPropMapping = {
  accessGroupConstraints: "access_group_constraints",
  notifications: "notifications",
  pdfConstraints: "pdf_constraints",
  stepActionConstraints: "action_constraints_v2",
  visibilityConstraints: "constraints",
  versionNamingConstraints: "version_naming_constraints_v2"
} as const;

const stepConstraintKeys = [
  "accessGroupConstraints",
  "notifications",
  "pdfConstraints",
  "stepActionConstraints",
  "visibilityConstraints",
  "versionNamingConstraints"
] as const;

const stepConstraintKeysRaw: string[] = Object.values(constraintRawPropMapping);

export const stepModelCreator = (
  rawStep: ZodStep,
  { processId }: { processId: string }
) => {
  const {
    available_user_tags: availableUserTags,
    collection_workflow_config: collectionWorkflowConfig,
    deadline_mins: deadlineMins,
    deadline,
    default_threads: defaultThreads,
    dependent_steps: dependentSteps,
    dynamic_group_names: dynamicGroupNames,
    extra,
    force_integration_call: forceIntegrationCall,
    is_filterable: isFilterable,
    my_tasks_groups: myTasksGroups,
    name,
    pdf_config: pdfConfig,
    visible_cw_activity_log: shouldVisibleCWActivityLog,
    static_groups: staticGroups,
    tag,
    taggable_dynamic_group_names: taggableDynamicGroupNames,
    taggable_user_groups: taggableUserGroups,
    step_type: stepType,
    external_tag: externalTag,
    __cs_comment: __csComment,
    default_state: {
      is_enabled: isEnabledDefaultState,
      is_locked: isLockedDefaultState,
      ...restDefaultState
    } = {},
    fields: rawFields,
    ...restStep
  } = rawStep;

  // restStepWithoutConstraints will remain unknown
  const restStepWithoutConstraints = Object.entries(restStep).reduce(
    (acc: Record<string, unknown>, [key, value]) => {
      // remove known properties
      if (!stepConstraintKeysRaw.includes(key)) {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );

  const step: Step = {
    ...restStepWithoutConstraints,
    availableUserTags,
    collectionWorkflowConfig,
    deadlineMins,
    deadline,
    defaultThreads,
    dependentSteps: dependentSteps ? dependentSteps.split(",") : [],
    dynamicGroupNames,
    extra,
    fields: [],
    forceIntegrationCall,
    isFilterable: isFilterable === undefined ? true : isFilterable, //Ref: DS-101
    myTasksGroups,
    name,
    pdfConfig,
    visibleCWActivityLog: shouldVisibleCWActivityLog,
    staticGroups,
    tag,
    taggableDynamicGroupNames,
    taggableUserGroups,
    stepType: stepType as StepTypes,
    externalTag,
    __cs_comment: __csComment,
    defaultState: rawStep.default_state
      ? {
          ...restDefaultState,
          isEnabled: isEnabledDefaultState,
          isLocked: isLockedDefaultState
        }
      : undefined,
    id: uuidWithProcessId(processId),
    __modifiedAt: Date.now(),
    __createdAt: Date.now(),
    __tagReadonly: tag ? true : false
  };

  const fieldsRawArray = Object.entries(rawFields)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(fieldKV => fieldKV[1]); // 1 because 0 is key, 1 is value

  let aggregatedRuleMap: RuleMap = {};
  let aggregatedConditionGroupMap: ConditionGroupMap = {};
  let aggregatedConditionMap: ConditionMap = {};
  let aggregatedNewFormatForChildMapping: Process["process"]["childMappings"] =
    {};
  // Now we parse all constraints and populate them accordingly.
  stepConstraintKeys.forEach(constraintKey => {
    if (!rawStep[constraintRawPropMapping[constraintKey]]) {
      return;
    }
    const { ruleMap, conditionGroupMap, conditionMap } = getRulesModel(
      rawStep[constraintRawPropMapping[constraintKey]],
      constraintRuleTypeMapping[constraintKey],
      { processId, targetFieldTag: step.tag }
    );

    aggregatedRuleMap = { ...aggregatedRuleMap, ...ruleMap };
    aggregatedConditionGroupMap = {
      ...aggregatedConditionGroupMap,
      ...conditionGroupMap
    };
    aggregatedConditionMap = { ...aggregatedConditionMap, ...conditionMap };

    step[constraintKey] = Object.keys(ruleMap);
  });

  const fieldsArray = fieldsRawArray.map(rawField => {
    const { field, ruleMap, conditionMap, conditionGroupMap } =
      fieldModelCreator(rawField, { processId });
    aggregatedNewFormatForChildMapping = {
      ...aggregatedNewFormatForChildMapping,
      ...(field.extra?.childMappingForAllLevels ?? {})
    };
    delete field.extra?.childMappingForAllLevels;
    aggregatedRuleMap = { ...aggregatedRuleMap, ...ruleMap };
    aggregatedConditionGroupMap = {
      ...aggregatedConditionGroupMap,
      ...conditionGroupMap
    };
    aggregatedConditionMap = { ...aggregatedConditionMap, ...conditionMap };

    return field;
  });

  const aggregatedFieldsMap: Record<string, Field> = fieldsArray.reduce(
    (agg, field) => ({ ...agg, [field.id]: field }),
    {}
  );

  step.fields = fieldsArray.map(field => field.id);

  return {
    step,
    fieldsMap: aggregatedFieldsMap,
    ruleMap: aggregatedRuleMap,
    conditionGroupMap: aggregatedConditionGroupMap,
    conditionMap: aggregatedConditionMap,
    childMappings: aggregatedNewFormatForChildMapping
  };
};

export const stepModelCreatorAppJSON = (
  rawStep: ZodStepAppJSON,
  { processId }: { processId: string }
) => {
  const {
    available_user_tags: availableUserTags,
    collection_workflow_config: collectionWorkflowConfig,
    deadline_mins: deadlineMins,
    deadline,
    default_threads: defaultThreads,
    dependent_steps: dependentSteps,
    dynamic_group_names: dynamicGroupNames,
    extra,
    force_integration_call: forceIntegrationCall,
    is_filterable: isFilterable,
    my_tasks_groups: myTasksGroups,
    name,
    pdf_config: pdfConfig,
    visible_cw_activity_log: shouldVisibleCWActivityLog,
    static_groups: staticGroups,
    tag,
    taggable_dynamic_group_names: taggableDynamicGroupNames,
    taggable_user_groups: taggableUserGroups,
    step_type: stepType,
    external_tag: externalTag,
    __cs_comment: __csComment,
    default_state: {
      is_enabled: isEnabledDefaultState,
      is_locked: isLockedDefaultState,
      ...restDefaultState
    } = {},
    fields: rawFields,
    __studio_id: id,
    __studio_modified_at: __studioModifiedAt,
    __studio_created_at: __studioCreatedAt,
    ...restStep
  } = rawStep;

  // restStepWithoutConstraints will remain unknown that's why it's typed as Record<string, any>
  const restStepWithoutConstraints = Object.entries(restStep).reduce(
    (acc: Record<string, any>, [key, value]) => {
      // remove known properties
      if (!stepConstraintKeysRaw.includes(key)) {
        acc[key] = value;
      }
      return acc;
    },
    {}
  );

  const step: Step = {
    ...restStepWithoutConstraints,
    availableUserTags,
    collectionWorkflowConfig,
    deadlineMins,
    deadline,
    defaultThreads,
    dependentSteps: dependentSteps ? dependentSteps.split(",") : [],
    dynamicGroupNames,
    extra,
    fields: [],
    forceIntegrationCall,
    isFilterable: isFilterable === undefined ? true : isFilterable, //Ref: DS-101
    myTasksGroups,
    name,
    pdfConfig,
    visibleCWActivityLog: shouldVisibleCWActivityLog,
    staticGroups,
    tag,
    taggableDynamicGroupNames,
    taggableUserGroups,
    stepType: stepType as StepTypes,
    externalTag,
    __cs_comment: __csComment,
    defaultState: rawStep.default_state
      ? {
          ...restDefaultState,
          isEnabled: isEnabledDefaultState,
          isLocked: isLockedDefaultState
        }
      : undefined,
    id: id ?? uuidWithProcessId(processId),
    __modifiedAt: __studioModifiedAt || Date.now(),
    __createdAt: __studioCreatedAt || Date.now(),
    __tagReadonly: tag ? true : false
  };

  let aggregatedNewFormatForChildMapping: Process["process"]["childMappings"] =
    {};

  const fieldsArray = rawFields.map(rawField => {
    const { field } = fieldModelCreator(rawField, { processId });
    aggregatedNewFormatForChildMapping = {
      ...aggregatedNewFormatForChildMapping,
      ...(field.extra?.childMappingForAllLevels ?? {})
    };
    delete field.extra?.childMappingForAllLevels;

    return field;
  });

  const aggregatedFieldsMap: Record<string, Field> = fieldsArray.reduce(
    (agg, field) => ({ ...agg, [field.id]: field }),
    {}
  );

  step.fields = fieldsArray.map(field => field.id);

  return {
    step,
    fieldsMap: aggregatedFieldsMap,
    childMappings: aggregatedNewFormatForChildMapping
  };
};
